<template>
    <div v-if="proposalStepValidation && briefAcceptedCheck" class="step-card-wrapper">
        <base-box border-radius="large" border :border-color="cardBorderColor" :class="classes" v-on="$listeners">
            <div slot="content">
                <g-level vertical-align="top" class="flex-fix">
                    <g-level-item class="flex-fix">
                        <div>
                            <g-typography
                                tag="h4"
                                :color="titleFontColor"
                                font-weight="semibold"
                                :value="title"
                                class="g-mb-3"
                            />
                            <g-alert
                                v-if="briefFilesAmount >= uploadsLimit"
                                class="step-card-uploads-description"
                                type="basic"
                            >
                                <g-row slot="message" type="flex" align="top">
                                    {{ heading }}
                                </g-row>
                            </g-alert>
                            <p v-else class="step-card-uploads-description">{{ heading }}</p>
                        </div>
                    </g-level-item>
                    <g-level-item slot="right">
                        <g-upload
                            v-if="briefFilesAmount < uploadsLimit"
                            :action="deliverFileUrl"
                            :headers="uploadHeaders"
                            :multiple="false"
                            :show-upload-list="{ showRemoveIcon: false }"
                            class="g-upload-wrapper pdf-ignore"
                            @change="handleFileUpload"
                        >
                            <g-button shape="round" theme="live" style="height: 40px; width: 40px">
                                <g-icon name="upload" />
                            </g-button>
                        </g-upload>
                    </g-level-item>
                </g-level>
                <div v-for="file in briefFiles" :key="file.id">
                    <div v-if="file.type === FILE_UPLOAD_TYPE" class="file-row">
                        <p class="file-text">{{ file.original_name }}</p>
                        <g-button
                            class="pdf-ignore"
                            theme="danger"
                            icon="trash-alt"
                            icon-size="small"
                            @click="handleShowFileDeleteModal(file.id, file.activation_id, file.original_name)"
                        />
                    </div>
                </div>

                <generic-confirmation-modal
                    :visible="showDeleteFileModal"
                    confirm-button-text="Yes"
                    :closable="false"
                    @cancel="handleFileDeleteCancel"
                    @continue="handleFileDelete(fileModalData.activation_id, fileModalData.id)"
                >
                    <template #title> Delete file? </template>
                    You're about to delete {{ fileModalData.original_name }}. This cannot be undone.
                </generic-confirmation-modal>
            </div>
        </base-box>
    </div>
</template>

<script>
    import { NAME_UPLOAD_FILES } from '../../../../../constants/activation-brief-steps';
    import { FILE_UPLOAD_TYPE } from '../../../../../constants/activation-files';
    import BaseBox from '../../../../library/BaseBox.vue';
    import UploadMixin from '../../mixins/ActivationUploadsMixin';
    import { get } from 'lodash';
    import { useContactStore } from '../../../../../../stores';
    import GenericConfirmationModal from '../../../activation-briefs/partials/GenericConfirmationModal.vue';

    export default {
        name: 'UploadFilesStepCardView',
        mixins: [UploadMixin],
        components: {
            BaseBox,
            GenericConfirmationModal
        },
        data() {
            return {
                showDeleteFileModal: false,
                fileModalData: {
                    id: '',
                    activation_id: '',
                    original_name: ''
                },
                uploadsLimit: 5,
                FILE_UPLOAD_TYPE
            };
        },
        computed: {
            stepName() {
                return NAME_UPLOAD_FILES;
            },
            subtitle() {
                return 'Complete';
            },
            title() {
                return 'Upload Files';
            },
            heading() {
                if (this.briefFilesAmount < this.uploadsLimit) {
                    return 'You may upload additional files to share with the brand below.';
                } else {
                    return `You\’ve reached your limit of ${this.uploadsLimit} uploads per brief. Please delete one and try again.`;
                }
            },
            titleFontColor() {
                return this.isCurrent ? 'white' : 'normal';
            },
            deliverFileUrl() {
                if (this.briefFilesAmount < this.uploadsLimit) {
                    return `/activations/${this.activationId}/brief/${this.briefId}/member/${this.member_id}/upload/file`;
                } else {
                    return null;
                }
            },
            getFiles() {
                const contact = useContactStore().data;
                return useActivations().getUploadedFiles(this.activationId, contact.account_id);
            },
            activationId() {
                return this.$attrs.brief.activation_id;
            },
            briefId() {
                return this.$attrs.brief.id;
            },
            member_id() {
                return this.$attrs.brief.member_id;
            },
            briefFiles() {
                const files = this.$attrs.brief && Array.isArray(this.$attrs.brief.files) ? this.$attrs.brief.files : [];
                return files;
            },
            briefFilesAmount() {
                const files = this.$attrs.brief && Array.isArray(this.$attrs.brief.files) ? this.$attrs.brief.files : [];
                const uploaded = files.filter((file) => file.type === FILE_UPLOAD_TYPE);
                return uploaded?.length || 0;
            },
            loadingFiles() {
                return false;
            },
            proposalStepValidation() {
                if (!this.$attrs.brief?.activated_at) {
                    return !this.$attrs.steps?.compensation;
                } else {
                    return true;
                }
            },
            classes() {
                return {
                    'g-p-6': true,
                    [`background-color-${this.state}`]: true,
                    'step-card-current': this.isCurrent
                };
            },
            cardBorderColor() {
                if (this.isError) {
                    return 'red';
                }
                return 'default';
            },
            briefAcceptedCheck() {
                return this.$attrs.brief?.status === 'accepted';
            }
        },
        setup() {
            function handleShowFileDeleteModal(id, activation_id, original_name) {
                this.fileModalData.id = id;
                this.fileModalData.activation_id = activation_id;
                this.fileModalData.original_name = original_name;
                this.showDeleteFileModal = true;
            }
            function handleFileDeleteCancel() {
                this.fileModalData.id = '';
                this.fileModalData.original_name = '';
                this.showDeleteFileModal = false;
            }
            function handleFileDelete(activationId, id) {
                this.showDeleteFileModal = false;
                this.$http.delete(`/api/v1/activations/${activationId}/files/${id}`);
                this.$router.go();
            }

            function handleFileUpload(res) {
                const status = get(res, 'file.status');

                if (status === 'done') {
                    this.$router.go();
                }
            }

            return {
                handleShowFileDeleteModal,
                handleFileDeleteCancel,
                handleFileDelete,
                handleFileUpload
            };
        }
    };
</script>

<style lang="scss" scoped>
    .step-card-wrapper {
        margin-bottom: $g-spacing-5;
    }
    .ant-btn .g-icon {
        margin-right: 0 !important;
    }
    ::v-deep.g-upload-wrapper {
        .ant-upload-list {
            display: none;
        }
    }
    .file-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 50px;
        box-shadow: 0px 1px 0px #e7e7e7;
    }
    .file-text {
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
        width: 80%;
    }
    .step-card-uploads-description {
        margin-bottom: 16px;
    }
</style>
